/**
 * Make recurring (design I modal, 620): turns an issued invoice into a
 * schedule. The form, summary sentence, validation and POST body live in
 * recurringModel; the server copies the invoice into the schedule, so later
 * edits to the invoice never change what the schedule makes.
 */
import React, { useEffect, useMemo, useState } from 'react';
import InvoiceRepository, { Channel, Recurring } from '../../../internals/repository/InvoiceRepository';
import { InvoiceApiError } from '../../../internals/repository/authedPosApi';
import { useInvoiceSettings } from '../useInvoicing';
import { money, todayColombo } from '../format';
import { glyphOf } from '../deliveryModel';
import { ChannelGlyph } from './DeliveryTag';
import { Dialog, ErrorBanner } from './VoidModal';
import {
  CHANNEL_PILL, FREQUENCIES, MODES, RECURRING_FORBIDDEN, SEND_ORDER,
  buildRecurringInput, customerName, defaultName, initialForm, isForbidden, nextPeriodStart, recurringFieldMessage,
  scheduleSummary, sendOrder, validateRecurring,
  type RecurringForm, type RecurringSource,
} from '../recurringModel';

const labelCls = 'flex flex-col gap-1.5 text-[13px] font-semibold text-text2';
const errStyle = (e?: string): React.CSSProperties | undefined => (e ? { borderColor: 'var(--neg)' } : undefined);

function FieldError({ text }: { text?: string }) {
  if (!text) return null;
  return <span role="alert" className="text-[12px] font-medium" style={{ color: 'var(--negFg)' }}>{text}</span>;
}

function Pill({ on, onClick, disabled, title, children }: { on: boolean; onClick: () => void; disabled?: boolean; title?: string; children: React.ReactNode }) {
  return (
    <button
      type="button"
      aria-pressed={on}
      disabled={disabled}
      title={title}
      onClick={onClick}
      className={`inline-flex h-[34px] items-center gap-1.5 rounded-full border px-3.5 text-[13px] font-semibold transition-colors focus-visible:outline-none focus-visible:shadow-[0_0_0_3px_var(--brandSoft)] disabled:cursor-not-allowed disabled:opacity-50 ${on ? 'border-brand bg-brand-soft text-brand' : 'border-borderc2 bg-surface text-text1 hover:bg-surface2'}`}
    >
      {children}
    </button>
  );
}

export default function MakeRecurringModal({
  open, onClose, shopId, fromDoc, onCreated,
}: {
  open: boolean;
  onClose: () => void;
  shopId: string;
  fromDoc: RecurringSource | null;
  onCreated: (recurring: Recurring) => void;
}) {
  if (!open || !fromDoc) return null;
  return <MakeRecurringForm key={fromDoc.docId} onClose={onClose} shopId={shopId} fromDoc={fromDoc} onCreated={onCreated} />;
}

function MakeRecurringForm({
  onClose, shopId, fromDoc, onCreated,
}: {
  onClose: () => void;
  shopId: string;
  fromDoc: RecurringSource;
  onCreated: (recurring: Recurring) => void;
}) {
  const today = todayColombo();
  const { settings } = useInvoiceSettings(shopId);
  const [form, setForm] = useState<RecurringForm>(() => initialForm(fromDoc, undefined, today));
  const [nameEdited, setNameEdited] = useState(false);
  const [startEdited, setStartEdited] = useState(false);
  const [chEdited, setChEdited] = useState(false);
  const [tried, setTried] = useState(false);
  const [touched, setTouched] = useState<Set<string>>(new Set());
  const [serverErrors, setServerErrors] = useState<Record<string, string>>({});
  const [banner, setBanner] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [available, setAvailable] = useState<Partial<Record<Channel, boolean>> | null>(null);

  // Send-by starts from the shop's default channels once settings arrive.
  useEffect(() => {
    if (settings?.defaultChannels?.length && !chEdited) {
      setForm((f) => ({ ...f, channels: sendOrder(settings.defaultChannels as string[]) }));
    }
  }, [settings, chEdited]);

  // Which channels Netlise can send on for this shop; email always works.
  useEffect(() => {
    let live = true;
    InvoiceRepository.credits(shopId)
      .then((c) => { if (live) setAvailable(c.channels ?? null); })
      .catch(() => { /* leave every channel enabled; the run reports a channel that is not set up */ });
    return () => { live = false; };
  }, [shopId]);

  const off = (c: Channel) => c !== 'EMAIL' && available != null && available[c] === false;
  // A channel that is not set up is never sent, whatever was ticked.
  const effective = useMemo(
    () => ({ ...form, channels: form.channels.filter((c) => c === 'EMAIL' || !available || available[c] !== false) }),
    [form, available],
  );

  const problems = useMemo(() => validateRecurring(effective, today), [effective, today]);
  const err = (k: string) => serverErrors[k] ?? (tried || touched.has(k) ? problems[k] : undefined);
  const touch = (k: string) => setTouched((t) => (t.has(k) ? t : new Set(t).add(k)));

  const update = (patch: Partial<RecurringForm>) => {
    setForm((f) => ({ ...f, ...patch }));
    setServerErrors({});
    setBanner(null);
  };

  const pickFrequency = (frequency: RecurringForm['frequency']) => update({
    frequency,
    ...(nameEdited ? {} : { name: defaultName(fromDoc, frequency) }),
    ...(startEdited ? {} : { startDate: nextPeriodStart(frequency, today) }),
  });

  const toggleChannel = (c: Channel) => {
    setChEdited(true);
    touch('channels');
    update({ channels: sendOrder(form.channels.includes(c) ? form.channels.filter((x) => x !== c) : [...form.channels, c]) });
  };

  const submit = async () => {
    setTried(true);
    if (Object.keys(problems).length || busy) {
      setBanner('Fix the highlighted fields, then try again.');
      return;
    }
    setBusy(true);
    setBanner(null);
    try {
      const res = await InvoiceRepository.createRecurring(shopId, buildRecurringInput(effective, fromDoc.docId));
      onCreated(res.recurring);
    } catch (e: any) {
      if (isForbidden(e)) setBanner(RECURRING_FORBIDDEN);
      else if (e instanceof InvoiceApiError && e.fields && Object.keys(e.fields).length) {
        const fields: Record<string, string> = {};
        Object.entries(e.fields).forEach(([k, code]) => { fields[k] = recurringFieldMessage(k, String(code)); });
        setServerErrors(fields);
        setBanner(fields.fromDocId ?? 'Some values were not accepted. Check the highlighted fields.');
      } else setBanner(e?.message || 'Could not start the schedule. Try again.');
      setBusy(false);
    }
  };

  const sub = `Creates a new invoice from ${fromDoc.number ?? 'this invoice'} for ${customerName(fromDoc.customer)}`
    + `${typeof fromDoc.totalMinor === 'number' ? ` at ${money(fromDoc.totalMinor)}` : ''} each time.`;

  return (
    <Dialog
      title="Make recurring"
      sub={sub}
      width={620}
      onClose={onClose}
      busy={busy}
      footer={(
        <>
          <button type="button" className="nl-btn nl-btn--secondary" onClick={onClose} disabled={busy}>Cancel</button>
          <button type="button" className="nl-btn nl-btn--primary" onClick={submit} disabled={busy}>
            {busy ? 'Starting…' : 'Start schedule'}
          </button>
        </>
      )}
    >
      <form className="grid grid-cols-1 gap-3.5 sm:grid-cols-2" onSubmit={(e) => { e.preventDefault(); submit(); }} noValidate>
        <label className={`${labelCls} sm:col-span-2`}>
          Name
          <input
            className="nl-input font-normal"
            value={form.name}
            maxLength={120}
            aria-invalid={!!err('name')}
            style={errStyle(err('name'))}
            onBlur={() => touch('name')}
            onChange={(e) => { setNameEdited(true); update({ name: e.target.value }); }}
          />
          <FieldError text={err('name')} />
        </label>

        <div role="group" aria-labelledby="rec-freq" className={`${labelCls} sm:col-span-2`}>
          <span id="rec-freq">Frequency</span>
          <div className="flex flex-wrap gap-1.5">
            {FREQUENCIES.map((f) => (
              <Pill key={f.key} on={form.frequency === f.key} onClick={() => pickFrequency(f.key)}>{f.label}</Pill>
            ))}
          </div>
        </div>

        <label className={labelCls}>
          First invoice
          <input
            type="date"
            className="nl-input font-normal"
            value={form.startDate}
            min={today}
            aria-invalid={!!err('startDate')}
            style={errStyle(err('startDate'))}
            onBlur={() => touch('startDate')}
            onChange={(e) => { setStartEdited(true); update({ startDate: e.target.value }); }}
          />
          <FieldError text={err('startDate')} />
        </label>

        <label className={labelCls}>
          Ends
          <select
            className="nl-select font-normal"
            value={form.endMode}
            onChange={(e) => update({ endMode: e.target.value as RecurringForm['endMode'] })}
          >
            <option value="NEVER">Never</option>
            <option value="DATE">On a date</option>
            <option value="COUNT">After a number of invoices</option>
          </select>
        </label>

        {form.endMode === 'DATE' && (
          <label className={labelCls}>
            End date
            <input
              type="date"
              className="nl-input font-normal"
              value={form.endDate}
              min={form.startDate || today}
              aria-invalid={!!err('end.date')}
              style={errStyle(err('end.date'))}
              onBlur={() => touch('end.date')}
              onChange={(e) => update({ endDate: e.target.value })}
            />
            <FieldError text={err('end.date')} />
          </label>
        )}
        {form.endMode === 'COUNT' && (
          <label className={labelCls}>
            Number of invoices
            <input
              className="nl-input font-mono font-normal"
              inputMode="numeric"
              value={form.count}
              aria-invalid={!!err('end.count')}
              style={errStyle(err('end.count'))}
              onBlur={() => touch('end.count')}
              onChange={(e) => update({ count: e.target.value.replace(/[^\d]/g, '').slice(0, 4) })}
            />
            <FieldError text={err('end.count')} />
          </label>
        )}

        <div role="radiogroup" aria-labelledby="rec-mode" className={`${labelCls} gap-2 sm:col-span-2`}>
          <span id="rec-mode">Each time</span>
          {MODES.map((m) => {
            const on = form.mode === m.key;
            return (
              <button
                key={m.key}
                type="button"
                role="radio"
                aria-checked={on}
                onClick={() => update({ mode: m.key })}
                className={`grid grid-cols-[18px_minmax(0,1fr)] gap-2.5 rounded-[10px] border-[1.5px] px-3 py-2.5 text-left transition-colors focus-visible:outline-none focus-visible:shadow-[0_0_0_3px_var(--brandSoft)] ${on ? 'border-brand bg-brand-soft' : 'border-borderc bg-surface hover:bg-surface2'}`}
              >
                <span className={`mt-px grid h-4 w-4 place-items-center rounded-full border-2 ${on ? 'border-brand' : 'border-borderc2'}`}>
                  <span className={`h-1.5 w-1.5 rounded-full ${on ? 'bg-brand' : ''}`} />
                </span>
                <span className="flex flex-col">
                  <span className="text-[14px] font-semibold text-text1">{m.label}</span>
                  <span className="text-[12px] font-normal text-text2">{m.desc}</span>
                </span>
              </button>
            );
          })}
        </div>

        {form.mode === 'ISSUE_AND_SEND' && (
          <div role="group" aria-labelledby="rec-send" className="flex flex-col gap-1.5 sm:col-span-2">
            <div className="flex flex-wrap items-center gap-2 text-[13px] font-semibold text-text2">
              <span id="rec-send">Send by</span>
              {SEND_ORDER.map((c) => (
                <Pill
                  key={c}
                  on={form.channels.includes(c) && !off(c)}
                  disabled={off(c)}
                  title={off(c) ? 'Not set up yet' : undefined}
                  onClick={() => toggleChannel(c)}
                >
                  <ChannelGlyph glyph={glyphOf(c)} />
                  {CHANNEL_PILL[c]}
                </Pill>
              ))}
            </div>
            <FieldError text={err('channels')} />
            {SEND_ORDER.some(off) && (
              <span className="text-[12px] font-normal text-text3">
                {SEND_ORDER.filter(off).map((c) => CHANNEL_PILL[c]).join(' and ')} not set up yet for sending by Netlise.
              </span>
            )}
          </div>
        )}

        <div role="status" aria-live="polite" className="rounded-[10px] bg-brand-soft px-3.5 py-3 text-[13px] font-medium text-brand sm:col-span-2">
          {scheduleSummary(effective)}
        </div>

        {banner && <div className="sm:col-span-2"><ErrorBanner text={banner} /></div>}
      </form>
    </Dialog>
  );
}
